// components/ui/control.ts
// The control-size contract (UI Phase 2a, docs/plans/ui-modernization/02-control-uniformity.md).
//
// WHY HEIGHT AND NOT PADDING
//   On 2026-09-29 the header rendered four controls at four heights — 32 / 34 /
//   42 / 48px — even though each one "looked right" in isolation. Nobody chose
//   those numbers; they fell out of arithmetic. `py-2 text-sm` is 36px on a
//   borderless control and 38px on a bordered one, so two controls with the
//   SAME padding recipe disagree the moment one of them gains a border. Line
//   height, border width and icon size all feed in, and every author has to
//   redo the sum.
//
//   An explicit height ends the argument: `h-9` is 36px whatever is inside it.
//   Padding then becomes what it should be — horizontal breathing room, chosen
//   per context — instead of the mechanism that accidentally sets height.
//
// WHAT IS THE CONTRACT
//   Height, radius and transition. NOT padding and NOT color: a header
//   dropdown trigger is legitimately tighter (`px-3`) than a primary call to
//   action (`px-4`), and intent color is the status contract's business.
//
// HOW TO ADOPT
//   A real button → use `<Button>`, which already consumes this.
//   Anything else that reads as a control (a dropdown trigger, a static badge
//   that must line up with its neighbours, an icon button) → spread
//   `controlClasses()` and add your own padding and color:
//
//     className={controlClasses('md', 'px-3 bg-surface-card border border-line-soft')}
//
//   Do not hand-write `h-9 … rounded-control … duration-fast` again. That
//   string existing in four places across three files is what this replaces.

import { cn } from './cn';
import type { ClassValue } from './cn';

export type ControlSize = 'sm' | 'md';

/**
 * Explicit control heights. These are the only two sizes; a third would mean
 * the visual hierarchy grew a tier, which is a design decision, not a one-off.
 *
 * Tailwind's box-sizing is `border-box`, so these are OUTER heights — a
 * bordered and a borderless control at the same size now agree, which is
 * exactly what padding arithmetic could not deliver.
 */
export const CONTROL_HEIGHT: Record<ControlSize, string> = {
  sm: 'h-8', // 32px — dense rows, secondary affordances
  md: 'h-9', // 36px — the default; the header row's shared height
};

/**
 * Everything a control shares regardless of size, intent or padding.
 *
 * `items-center` is here rather than in the caller because it is what makes an
 * explicit height safe: with a fixed height and no vertical centering, content
 * sits at the top of the box.
 */
export const CONTROL_BASE =
  'inline-flex items-center rounded-control transition-colors duration-fast ease-out-soft';

/**
 * One-line adoption for a control that is not a `<Button>`.
 *
 * @param size  height tier; defaults to the `md` the header row settled on
 * @param extra per-usage classes — padding, color, gap, focus treatment
 */
export function controlClasses(
  size: ControlSize = 'md',
  ...extra: ClassValue[]
): string {
  return cn(CONTROL_BASE, CONTROL_HEIGHT[size], ...extra);
}
